'use client';

import { c as _c } from "react/compiler-runtime";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshAccessToken } from '@/shared/utils/auth';
import { useAppDispatch } from '@/store/hooks';
import { fetchNotifications } from '../store';
const MAX_AUTH_FAILURES = 3;
const MAX_RECONNECT_DELAY_MS = 30_000;
interface UseNotificationStreamParams {
  escritorioId: string | null | undefined;
}
export const useNotificationStream = t0 => {
  const $ = _c(15);
  const {
    escritorioId
  } = t0;
  const dispatch = useAppDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const abortControllerRef = useRef(null);
  const authFailureCountRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const hasConnectedBeforeRef = useRef(false);
  let t1;
  if ($[0] !== dispatch) {
    t1 = () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
      fetchDebounceRef.current = setTimeout(() => {
        fetchDebounceRef.current = null;
        dispatch(fetchNotifications("workspace"));
      }, 200);
    };
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scheduleDebouncedFetch = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = abortSignal => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, MAX_RECONNECT_DELAY_MS);
      reconnectAttemptsRef.current = reconnectAttemptsRef.current + 1;
      reconnectTimerRef.current = setTimeout(() => {
        if (!abortSignal.aborted) {
          setReconnectTrigger(_temp);
        }
      }, delay);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const scheduleReconnect = t2;
  let t3;
  if ($[3] !== escritorioId || $[4] !== scheduleDebouncedFetch) {
    t3 = async () => {
      if (!escritorioId) {
        return;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const baseUrl = process.env.NEXT_PUBLIC_API_URL;
      const streamUrl = `${baseUrl}/notifications/stream`;
      const headers = {
        Accept: "text/event-stream",
        "X-Workspace-Id": escritorioId
      };
      try {
        await fetchEventSource(streamUrl, {
          method: "GET",
          headers,
          credentials: "include",
          signal: abortController.signal,
          onopen: async response => {
            if (response.ok && response.headers.get("content-type")?.includes("text/event-stream")) {
              setIsConnected(true);
              authFailureCountRef.current = 0;
              reconnectAttemptsRef.current = 0;
              if (hasConnectedBeforeRef.current) {
                scheduleDebouncedFetch();
              }
              hasConnectedBeforeRef.current = true;
              return;
            }
            if (response.status === 401) {
              throw new Error("Unauthorized");
            }
            if (response.status === 429) {
              await new Promise(_temp2);
              throw new Error("Rate limited");
            }
            if (response.status === 503) {
              throw new Error("Realtime unavailable");
            }
            if (response.status >= 400 && response.status < 500) {
              throw new Error(`Fatal: ${response.status}`);
            }
          },
          onmessage: msg => {
            if (msg.event === "heartbeat" || msg.event === "connected") {
              return;
            }
            if (msg.event === "notification") {
              scheduleDebouncedFetch();
            }
          },
          onclose: () => {
            setIsConnected(false);
            if (!abortController.signal.aborted) {
              scheduleReconnect(abortController.signal);
            }
          },
          onerror: err => {
            setIsConnected(false);
            if (err instanceof Error && err.message === "Unauthorized") {
              authFailureCountRef.current = authFailureCountRef.current + 1;
              if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
                throw err;
              }
              refreshAccessToken().then(ok => {
                if (ok) {
                  authFailureCountRef.current = 0;
                  scheduleReconnect(abortController.signal);
                }
              });
              throw err;
            }
            scheduleReconnect(abortController.signal);
            throw err;
          }
        });
      } catch {
        setIsConnected(false);
      }
    };
    $[3] = escritorioId;
    $[4] = scheduleDebouncedFetch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const connect = t3;
  let t4;
  if ($[6] !== connect || $[7] !== escritorioId) {
    t4 = () => {
      if (!escritorioId) {
        setIsConnected(false);
        return;
      }
      connect();
      return () => {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
        }
        if (fetchDebounceRef.current) {
          clearTimeout(fetchDebounceRef.current);
        }
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        setIsConnected(false);
      };
    };
    $[6] = connect;
    $[7] = escritorioId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== connect || $[10] !== escritorioId || $[11] !== reconnectTrigger) {
    t5 = [escritorioId, connect, reconnectTrigger];
    $[9] = connect;
    $[10] = escritorioId;
    $[11] = reconnectTrigger;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== isConnected) {
    t6 = {
      isConnected
    };
    $[13] = isConnected;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
function _temp(v) {
  return v + 1;
}
function _temp2(resolve) {
  return setTimeout(resolve, 10000);
}